import { useState, useEffect, useCallback, useRef } from "react";
import { AuthError, ConsentRequest } from "@/hooks/auth/types";
import { logger } from "@/utils";
import * as apiClient from "@/utils/serenity-api-client";
import {
  firebaseAuth,
  googleAuthProvider,
  googleAuthSignIn,
  FirebaseUser
} from "@/utils/firebase";
import { Platform } from "react-native";
import { getUser, createUser, updateUserConsent } from "@/store/users";
import { User } from "@/store/types";
import { Timestamp } from "firebase/firestore";

/**
 * All user-facing error copy surfaced from this module. Grouped by the
 * flow that produces them so call sites read like
 * `ERROR_MESSAGES.signIn.network` instead of scattering literal strings
 * through the file.
 *
 * Keep these strings here — copy review and translation will eventually
 * walk this object, not the call sites.
 */
const ERROR_MESSAGES = {
  signIn: {
    /**
     * Wrap an underlying error message in a sign-in frame so the user
     * knows what *we* were doing when it failed — `error.message` alone
     * could be anything from "Missing or insufficient permissions" to
     * "Network request failed" and gives no context. If we have no
     * detail (the thrown value wasn't an Error), the parenthetical is
     * omitted rather than padded with another "sign in failed" string.
     */
    unexpected: (detail?: string) =>
      detail
        ? `We couldn't sign you in. Please try again. (${detail})`
        : "We couldn't sign you in. Please try again.",
    /** Failures in the Google popup / credential exchange flow. */
    google: {
      accountConflict: "An account with this email already exists.",
      invalidCredential:
        "The credential is invalid or has expired. Please try again."
    }
  },
  /** Sign-out failures. */
  signOut: {
    failed: "Sign out failed. Please try again."
  },
  /**
   * Firebase auth error codes from email/password flows. `byCode` is a
   * lookup keyed on Firebase's documented `code` strings; `fallback` is
   * used for codes we haven't mapped (and as the generic "something went
   * wrong" copy elsewhere in the module).
   */
  firebaseAuth: {
    byCode: {
      "auth/email-already-in-use": "An account with this email already exists.",
      "auth/weak-password": "Password must be at least 8 characters.",
      "auth/invalid-email": "Please enter a valid email address.",
      "auth/invalid-credential": "Incorrect email or password.",
      "auth/too-many-requests": "Too many failed attempts. Try again later."
    } as Record<string, string>,
    fallback: "Something went wrong. Please try again."
  }
} as const;

/**
 * Map a Firebase auth `error.code` (e.g. "auth/invalid-credential") to its
 * user-facing message.
 */
function mapFirebaseError(code: string): string {
  return (
    ERROR_MESSAGES.firebaseAuth.byCode[code] ??
    ERROR_MESSAGES.firebaseAuth.fallback
  );
}

/**
 * Create initial user document for first-time users
 */
const createInitialUserDocument = async (
  firebaseUser: FirebaseUser
): Promise<void> => {
  // Validate that we have required fields
  if (!firebaseUser.email) {
    throw new Error(
      "User email is required but not provided by authentication provider"
    );
  }

  const date = Timestamp.now();
  const userData: User = {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    phoneNumber: firebaseUser.phoneNumber,
    photoURL: firebaseUser.photoURL,
    providerId: firebaseUser.providerId,
    consent: {
      hasConsented: false
    },
    createdAt: date,
    updatedAt: date
  };

  await createUser(firebaseUser.uid, userData);
};

/**
 * Get current user's ID token
 */
const getIdToken = async (): Promise<string | null> => {
  const currentUser = firebaseAuth.currentUser;
  if (!currentUser) return null;

  try {
    return await currentUser.getIdToken();
  } catch (error) {
    logger.error("Failed to get ID token", error);
    return null;
  }
};

/**
 * Validate user with server
 */
const validateWithServer = async (
  idToken: string
): Promise<apiClient.AuthValidateResult> => {
  return await apiClient.validateAuth(idToken);
};

/**
 * Custom hook for managing authn, authz, and user consent.
 *
 * Provides Google Sign-in functionality with server-side validation and Firestore-based user data.
 * The user state represents both authenticated (Firebase) AND authorized (server) users.
 * User consent and profile data is managed through Firestore.
 *
 * @returns Authentication and authorization state and control functions
 * - user: Current authenticated AND authorized user, or null if not both
 * - isLoading: Whether authentication/authorization state is being determined
 * - error: Current authentication/authorization error, or null if none
 * - signInWithGoogle: Function to initiate Google Sign-in and authorization flow
 * - signOut: Function to sign out current user
 * - getIdToken: Function to get current user's Firebase ID token
 * - recordConsent: Function to record user consent in Firestore
 */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [waitlistedEmail, setWaitlistedEmail] = useState<string | null>(null);
  const [emailVerificationPending, setEmailVerificationPending] = useState<
    string | null
  >(null);
  const suppressAuthStateRef = useRef(false);

  // Listen to Firebase auth state changes and validate with server
  // User will only be set after successful server validation
  useEffect(() => {
    const stateChanged = firebaseAuth.onAuthStateChanged(
      async (firebaseUser: FirebaseUser | null) => {
        if (suppressAuthStateRef.current) return;

        logger.info(
          `Firebase auth state changed: ${firebaseUser?.uid ?? "null"}`
        );
        setIsLoading(true);

        // 1. Authn
        if (!firebaseUser) {
          // If Firebase user is gone, clear our user state
          setUser(null);
          setIsLoading(false);
          return;
        }

        // 1b. Block unverified email users
        if (!firebaseUser.emailVerified) {
          setEmailVerificationPending(firebaseUser.email);
          await firebaseAuth.signOut();
          setIsLoading(false);
          return;
        }

        // Clear stale verification state on successful verified sign-in
        setEmailVerificationPending(null);

        try {
          // 2. Authz
          const idToken = await firebaseUser.getIdToken();
          const result = await validateWithServer(idToken);

          if (!result.success) {
            setUser(null);
            if (result.waitlisted) {
              // Capture email before signing out
              setWaitlistedEmail(firebaseUser.email);
              setError(null);
            } else {
              // The API client owns this copy — its `errorPrefix` /
              // network-catch message is already user-facing.
              setError(result.message);
            }
            await firebaseAuth.signOut();
            return;
          }

          logger.info("User is authorized");

          // 3. Store / fetch user data in Firestore
          let user = await getUser(firebaseUser.uid);
          if (!user) {
            await createInitialUserDocument(firebaseUser);
            logger.info("New user. User document created");
            // Fetch the latest user data from Firestore
            user = await getUser(firebaseUser.uid);
          } else {
            logger.info("Existing user");
          }

          setUser(user);
          setError(null);
        } catch (error) {
          logger.error("Server authorization failed", error);
          setUser(null);
          // We don't know what produced this exception (getUser, Firestore,
          // createInitialUserDocument, getIdToken, …) — wrap the underlying
          // message in a sign-in frame so the user has context, while still
          // surfacing the detail to them and to Sentry.
          setError(
            ERROR_MESSAGES.signIn.unexpected(
              error instanceof Error ? error.message : undefined
            )
          );
          // Sign out from Firebase if server validation fails
          await firebaseAuth.signOut();
        } finally {
          setIsLoading(false);
        }
      }
    );

    return () => {
      stateChanged();
    };
  }, []);

  // Sign in with Google
  const signInWithGoogle = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      setWaitlistedEmail(null);

      // Check if device supports Google Play services (Android only)
      if (Platform.OS === "android" && googleAuthSignIn.hasPlayServices) {
        await googleAuthSignIn.hasPlayServices({
          showPlayServicesUpdateDialog: true
        });
      }

      // Step 1: Sign in with Google
      // For web: this already completes the full Firebase authentication via signInWithPopup
      // For native: this gets the Google credential that we need to exchange with Firebase
      const googleSignInResult = await googleAuthSignIn.signIn();

      if (Platform.OS === "web") {
        // For web: user is already authenticated with Firebase via signInWithPopup
        // The Firebase auth state should have updated automatically
        // onAuthStateChanged will handle server validation and user state
      } else {
        // For native: we need to manually exchange the Google credential with Firebase
        if (!googleSignInResult.data?.idToken) {
          throw new Error("Failed to get Google ID token");
        }

        const googleCredential = googleAuthProvider.credential(
          googleSignInResult.data.idToken
        );

        await firebaseAuth.signInWithCredential(googleCredential);
        // onAuthStateChanged will handle server validation and user state
      }

      // Step 2: Firebase authentication is complete
      // onAuthStateChanged will automatically handle server validation and set user state
      // isLoading will remain true until onAuthStateChanged completes the full flow
    } catch (error: unknown) {
      logger.error("Sign in failed", error);
      setIsLoading(false);

      // Handle specific Firebase auth errors
      const authError = error as AuthError;
      if (authError.code === "auth/account-exists-with-different-credential") {
        // This error can occur when the user has already signed up with a different auth provider
        logger.error("Account exists with different credential", error);

        setError(ERROR_MESSAGES.signIn.google.accountConflict);
        return;
      }

      if (authError.code === "auth/invalid-credential") {
        logger.error("Invalid credential", error);
        setError(ERROR_MESSAGES.signIn.google.invalidCredential);
        return;
      }

      // Sign out from Firebase if anything failed
      await firebaseAuth.signOut();

      // Same wrap-with-detail pattern as the onAuthStateChanged catch
      // above: we don't know what produced the error, so frame it as
      // sign-in and append the underlying detail if we have one.
      setError(
        ERROR_MESSAGES.signIn.unexpected(
          error instanceof Error ? error.message : undefined
        )
      );
    }
  }, []);

  // Sign out
  const signOut = useCallback(async (): Promise<void> => {
    try {
      await firebaseAuth.signOut();
      if (Platform.OS !== "web") {
        await googleAuthSignIn.signOut();
      }
      setUser(null);
      setError(null);
      logger.info("Sign out successful");
    } catch (error) {
      logger.error("Sign out failed", error);
      setError(ERROR_MESSAGES.signOut.failed);
    }
  }, []);

  // Refresh user data from Firestore
  const refreshUserData = useCallback(async (): Promise<void> => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) return;

    try {
      const updatedUserData = await getUser(currentUser.uid);
      if (updatedUserData) {
        setUser(updatedUserData);
      }
    } catch (error) {
      logger.error("Error refreshing user data", error);
    }
  }, []);

  // Record user consent and update Firestore
  const recordConsent = useCallback(
    async (consentRequest: ConsentRequest): Promise<boolean> => {
      try {
        setIsLoading(true);

        const currentUser = firebaseAuth.currentUser;
        if (!currentUser) {
          logger.error("No authenticated user available for consent recording");
          return false;
        }

        // Update user document in Firestore with consent information
        await updateUserConsent(currentUser.uid, {
          hasConsented: true,
          consentVersion: consentRequest.version
        });

        await refreshUserData();

        logger.info("Consent recorded successfully in Firestore");
        return true;
      } catch (error) {
        logger.error("Error recording consent in Firestore", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [refreshUserData]
  );

  // Sign up with email and password
  const signUpWithEmail = useCallback(
    async (email: string, password: string): Promise<void> => {
      try {
        suppressAuthStateRef.current = true;
        setIsLoading(true);
        setError(null);
        const { user } = await firebaseAuth.createUserWithEmailAndPassword(
          email,
          password
        );
        await firebaseAuth.sendEmailVerification(user);
        await firebaseAuth.signOut();
        setEmailVerificationPending(email);
      } catch (error: unknown) {
        const authError = error as AuthError;
        setError(mapFirebaseError(authError.code));
      } finally {
        suppressAuthStateRef.current = false;
        setIsLoading(false);
      }
    },
    []
  );

  // Sign in with email and password
  // No suppress needed — onAuthStateChanged handles emailVerified check
  const signInWithEmail = useCallback(
    async (email: string, password: string): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        await firebaseAuth.signInWithEmailAndPassword(email, password);
        // onAuthStateChanged handles emailVerified check + server validation
      } catch (error: unknown) {
        const authError = error as AuthError;
        setError(mapFirebaseError(authError.code));
        setIsLoading(false);
      }
    },
    []
  );

  // Send password reset email
  const resetPassword = useCallback(async (email: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      await firebaseAuth.sendPasswordResetEmail(email);
      return true;
    } catch (error: unknown) {
      const authError = error as AuthError;
      setError(mapFirebaseError(authError.code));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Resend verification email — requires brief sign-in to get user object
  const resendVerificationEmail = useCallback(
    async (email: string, password: string): Promise<void> => {
      try {
        suppressAuthStateRef.current = true;
        setIsLoading(true);
        setError(null);
        const { user } = await firebaseAuth.signInWithEmailAndPassword(
          email,
          password
        );
        await firebaseAuth.sendEmailVerification(user);
        await firebaseAuth.signOut();
      } catch (error: unknown) {
        const authError = error as AuthError;
        setError(mapFirebaseError(authError.code));
        // Ensure we sign out if we got past signIn but failed on sendEmailVerification
        try {
          await firebaseAuth.signOut();
        } catch {
          // Ignore — may not be signed in
        }
      } finally {
        suppressAuthStateRef.current = false;
        setIsLoading(false);
      }
    },
    []
  );

  return {
    user,
    isLoading,
    error,
    waitlistedEmail,
    signInWithGoogle,
    signOut,
    getIdToken,
    recordConsent,
    refreshUserData,
    clearError: useCallback(() => setError(null), []),
    signUpWithEmail,
    signInWithEmail,
    resetPassword,
    emailVerificationPending,
    clearEmailVerificationPending: useCallback(
      () => setEmailVerificationPending(null),
      []
    ),
    resendVerificationEmail
  };
}
